<template>
  <main :aria-label="isEditMode ? $t('general.edit_program') : $t('general.add_program')" class="program-au-page">
    <CompanyBreadcrumb :aria-label="isEditMode ? $t('general.edit_program') : $t('general.add_program')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div class="section-header mb-lg-2 border-0 uk-flex-middle program-au-header">
      <div class="section-header-left">
        <h2 class="program-au-title">
          <span>{{ isEditMode ? $t('general.edit_program') : $t('general.new_program') }}</span>
        </h2>
      </div>
    </div>

    <template v-if="errorMessages && errorMessages.length">
      <div v-for="(error, index) in errorMessages"
           :key="index"
           class="card program-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p>
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </div>
    </template>

    <div v-if="successMessage" class="card program-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p>
          <i class="uil-check"></i> {{ successMessage }}
        </p>
      </div>
    </div>

    <div class="card program-au-card">
      <ul id="uk-switcher-tab"
          class="uk-child-width-expand uk-tab stepper-buttons program-au-tabs"
          uk-switcher="connect: #course-edit-tab ; animation: uk-animation-slide-left-medium, uk-animation-slide-right-medium">
        <li class="uk-active">
          <a href="#" aria-expanded="true">
            <i class="icon-feather-flag"></i>
            <b>{{ $t('general.home') }}</b>
          </a>
        </li>
        <li>
          <a href="#" aria-expanded="false">
            <i class="uil-book-open"></i>
            <b>{{ $t('general.contents') }}</b>
          </a>
        </li>
        <li>
          <a href="#" aria-expanded="false">
            <i class="uil-invoice"></i>
            <b>{{ $t('general.overview') }}</b>
          </a>
        </li>
      </ul>

      <default-loading v-if="isEditMode && (program_loading || !formattedProgramItem)"></default-loading>
      <template v-else>
        <form class="card-body program-au-form" novalidate>
          <ul id="course-edit-tab" class="uk-switcher uk-margin" style="touch-action: pan-y pinch-zoom">
            <!-- Tab 1: Ozellikler -->
            <li class="uk-active">
              <div class="row">
                <div class="col-xl-9 m-auto">
                  <div class="form-group row mb-3">
                    <label class="col-md-3 col-form-label" for="course_title">
                      {{ $t('general.program_title') }}
                      <span class="required">*</span>
                    </label>
                    <div class="col-md-9">
                      <input id="course_title"
                             v-model="form.title"
                             type="text"
                             class="form-control"
                             name="title"
                             :placeholder="$t('general.heading')"
                             required
                      />
                    </div>
                  </div>
                  <div class="form-group row mb-3">
                    <label class="col-md-3 col-form-label" for="short_description">
                      {{ $t('messages.description') }}
                      <span class="required">*</span>
                    </label>
                    <div class="col-md-9">
                      <textarea id="short_description"
                                v-model="form.description"
                                name="short_description"
                                class="form-control"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div class="program-au-footer">
                <button class="program-au-btn uk-button uk-button-primary small"
                        type="button"
                        @click="validateAndSwitch(1)">
                  <i class="icon-feather-arrow-right"></i> {{ $t('general.save_and_continue') }}
                </button>
              </div>
            </li>

            <!-- Tab 2: Icerik olustur -->
            <li>
              <div class="row">
                <div class="col-md-12">
                  <div class="col-xl-12">
                    <button type="button"
                            class="btn btn-secondary mb-3 w-100 program-au-chapter-btn"
                            @click="createContent()">
                      <i class="uil-plus m-0"></i> {{ $t('general.add_chapter') }}
                      <span class="required">*</span>
                    </button>
                  </div>

                  <div id="modal_learning"
                       v-if="showModalLearning"
                       class="uk-modal-full uk-modal program-modal"
                       uk-modal="">
                    <div class="uk-modal-footer bggray program-modal-header">
                      <h3 class="program-modal-title">
                        <i class="uil-presentation-plus"></i> {{ $t('general.select_content') }}
                      </h3>
                      <button class="uk-button uk-button-default small uk-modal-close program-modal-close"
                              type="button">
                        <i class="uil-times"></i> {{ $t('general.close') }}
                      </button>
                    </div>
                    <div class="uk-modal-dialog bggray program-modal-body">
                      <Content @addNewEducation="addEducationToContent($event)"
                               :show_survey="true"
                               @removeEducation="removeEducationFromContent($event)"
                               :selected_items="all_selected_educations"
                               current_page="programs"
                      ></Content>
                    </div>
                  </div>

                  <draggable class="row flex-column program-section-list"
                             :list="contents"
                             group="section_widget">
                    <div class="col-xl-12 mb-2"
                         v-for="(content, index) in contents"
                         :key="'section-' + content.id">
                      <ul class="c-curriculum" uk-accordion>
                        <li class="uk-open">
                          <div class="d-flex justify-content-between align-items-center program-section-header">
                            <div class="row program-section-row">
                              <div class="col-md-1 program-section-icon">
                                <i class="uil-folder"></i>
                              </div>
                              <div class="col-md-4 program-section-name">
                                <input v-model="content.name"
                                       type="text"
                                       id="content_name"
                                       name="name"
                                       v-on:keydown.enter.prevent
                                       :placeholder="$t('general.chapter_name')"
                                       required
                                />
                              </div>
                              <div class="col-md-7 program-section-actions">
                                <a class="btn btn-info btn-sm program-section-action-btn"
                                   href="javascript:void(0)"
                                   uk-toggle="target: #modal_learning"
                                   @click="active_content_id = content.id">
                                  <i class="uil-plus m-0"></i>{{ $t('general.select_content') }}<span class="required">*</span>
                                </a>
                                <a class="btn btn-info btn-sm ml-1 program-section-action-btn"
                                   href="javascript:void(0)"
                                   @click="removeContent(content.id)">
                                  <i class="uil-trash"></i>
                                </a>
                                <a class="uk-accordion-title" style="display:contents"></a>
                              </div>
                            </div>
                          </div>
                          <draggable class="uk-accordion-content"
                                     :list="content.educations"
                                     group="education_widget">
                            <div v-for="education in content.educations"
                                 :key="'edu-' + education.id">
                              <div class="sec-list-item">
                                <label class="mb-0 mx-2 program-edu-badge">
                                  {{ $t(education.name) }}
                                </label>
                                <div>
                                  <i class="uil-list-ul mr-2"></i>
                                  <p>{{ education.title }}</p>
                                </div>
                                <div>
                                  <div class="btn-act">
                                    <a href="#"
                                       :uk-tooltip="'title:' + $t('general.remove')"
                                       @click.prevent="removeEducationFromContent(education.id)">
                                      <i class="icon-feather-trash"></i>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </draggable>
                        </li>
                      </ul>
                    </div>
                  </draggable>

                  <div class="program-au-footer">
                    <button class="program-au-btn uk-button uk-button-outline small"
                            type="button"
                            @click="switcherChange(0)">
                      <i class="icon-feather-arrow-left"></i> {{ $t('general.previous') }}
                    </button>
                    <button class="program-au-btn uk-button uk-button-primary small"
                            type="button"
                            @click="validateAndSwitch(2)">
                      <i class="icon-feather-arrow-right"></i> {{ $t('general.save_and_continue') }}
                    </button>
                  </div>
                </div>
              </div>
            </li>

            <!-- Tab 3: Ozet -->
            <li>
              <div class="program-au-overview">
                <div class="program-au-overview__status">
                  <h2 class="program-au-overview__icon">
                    <i class="icon-feather-check-circle text-success"></i>
                  </h2>
                  <h3 class="program-au-overview__title">{{ $t('general.program_is_ready') }}</h3>
                </div>

                <div class="program-au-overview__table-wrap">
                  <table class="uk-table uk-table-divider program-result-table">
                    <tbody>
                      <tr>
                        <td class="program-result-label">{{ $t('general.program_title') }}:</td>
                        <td class="program-result-value">{{ form.title }}</td>
                      </tr>
                      <tr>
                        <td class="program-result-label">{{ $t('general.total_chapters') }}:</td>
                        <td class="program-result-value">{{ contents.length }}</td>
                      </tr>
                      <tr>
                        <td class="program-result-label">{{ $t('general.total_contents') }}:</td>
                        <td class="program-result-value">{{ all_selected_educations.length }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="program-au-footer">
                <button class="program-au-btn uk-button uk-button-outline"
                        type="button"
                        @click="switcherChange(1)">
                  <i class="icon-feather-arrow-left"></i> {{ $t('general.previous') }}
                </button>
                <button class="program-au-btn uk-button uk-button-primary small"
                        type="button"
                        @click="saveForm()">
                  <i class="icon-feather-arrow-right"></i> {{ $t('general.save') }}
                </button>
              </div>
            </li>
          </ul>
        </form>
      </template>
    </div>
  </main>
</template>

<script>
import store from "@/core/services";
import draggable from "vuedraggable";
import Swal from "sweetalert2";
import Content from "@//view/components/assignment/Content";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import module, {
  MODULE_NAME,
  CREATE_ITEM,
  UPDATE_ITEM,
  GET_ITEM,
  ITEM,
  BASE_URL,
  ERROR,
  LOADING,
} from "@/core/services/store/program.module";

export default {
  name: "ProgramAddUpdate",
  components: { Content, draggable, DefaultLoading, CompanyBreadcrumb },
  data() {
    return {
      showModalLearning: false,
      successMessage: "",
      errorMessages: [],
      all_selected_educations: [],
      contents: [],
      form: {
        title: "",
        description: "",
      },
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }
    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id;
    },
    breadcrumbItems() {
      return [
        { name: this.$t("general.programs") },
        { name: this.isEditMode ? this.$t("general.edit_program") : this.$t("general.add_program") },
      ];
    },
    program_item: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEM];
      },
      set(value) {},
    },
    program_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) {},
    },
    formattedProgramItem() {
      let self = this;
      let item = self.program_item;

      if (item == null || item.contents == undefined) return false;
      self.form.title = item.title;
      self.form.description = item.description;
      self.all_selected_educations = item.all_selected_educations;
      self.contents = item.contents;
      return true;
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) {},
    },
  },
  methods: {
    getProgramItem() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEM, {
        url: BASE_URL + "/" + this.$route.params.id,
      });
    },
    saveForm() {
      let self = this;
      let form = self.form;
      if (!self.isValidFields("ALL")) return false;

      if (self.isEditMode) {
        let result_contents = [];
        self.contents.forEach((section, section_index) => {
          let educations = [];
          section.educations.forEach((education, index) => {
            educations[index] = education.id;
          });
          result_contents[section_index] = {
            educations: educations,
            name: section.name,
          };
        });

        let formData = {
          title: form.title,
          description: form.description,
          contents: result_contents,
        };

        store
          .dispatch(MODULE_NAME + "/" + UPDATE_ITEM, {
            url: BASE_URL + "/" + this.$route.params.id,
            contents: formData,
          })
          .then(() => {
            this.scrollToTop();
            if (!self.error) {
              self.successMessage = self.$t("general.successfully_updated");
              self.$router.push({ name: "company.programs.all" });
            } else self.errorMessages.push(self.error);
          });
      } else {
        let formData = new FormData();
        formData.append("title", form.title);
        formData.append("description", form.description);

        self.contents.forEach((section, section_index) => {
          let count = 0;
          section.educations.forEach((education, index) => {
            count++;
            formData.append("contents[" + section_index + "][educations][" + index + "]", education.id);
          });
          if (count > 0)
            formData.append("contents[" + section_index + "][name]", section.name);
        });

        store
          .dispatch(MODULE_NAME + "/" + CREATE_ITEM, {
            url: BASE_URL,
            contents: formData,
          })
          .then(() => {
            this.scrollToTop();
            if (!self.error) {
              self.successMessage = self.$t("general.successfully_created");
              self.$router.push({ name: "company.programs.all" });
            } else self.errorMessages.push(self.error);
          });
      }
    },
    createContent() {
      Swal.fire({
        title: this.$t("general.chapter_name"),
        input: "text",
        inputAttributes: {
          autocapitalize: "off",
        },
        showCancelButton: true,
        confirmButtonText: this.$t("general.create"),
        confirmButtonColor: "#ae71ff",
        cancelButtonText: this.$t("general.cancel"),
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: (value) => {
          if (!value) {
            Swal.showValidationMessage(
              this.$t("validation.required_field").replace(":NAME", this.$t("general.chapter_name"))
            );
          }
        },
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          let length = this.contents.length;
          this.contents.push({
            id: length + 1,
            name: result.value,
            educations: [],
          });
        }
      });
    },
    addEducationToContent(education_item) {
      let self = this;
      let edu_id = education_item.id;
      let content_id = self.active_content_id;
      if (self.active_content_id == null) return false;
      self.contents.forEach(function (item, index) {
        if (item.id == content_id) {
          self.all_selected_educations.push(edu_id);
          self.contents[index].educations.push(education_item);
        }
      });
    },
    removeEducationFromContent(edu_id) {
      let content_index = null;
      for (const [index, value] of Object.entries(this.contents)) {
        content_index = value.educations
          .map(function (x) {
            return x.id;
          })
          .indexOf(edu_id);
        if (content_index != -1) {
          content_index = index;
          break;
        }
      }

      let educations_of_content = this.contents[content_index].educations;
      let educations_ids_all = this.all_selected_educations;
      let pos = educations_of_content
        .map(function (x) {
          return x.id;
        })
        .indexOf(edu_id);
      let pos_all = educations_ids_all
        .map(function (x) {
          return x;
        })
        .indexOf(edu_id);
      educations_of_content.splice(pos, 1);
      educations_ids_all.splice(pos_all, 1);
      return true;
    },
    removeContent(id) {
      let index = this.contents
        .map(function (x) {
          return x.id;
        })
        .indexOf(id);
      if (this.contents[index].educations)
        this.contents[index].educations.forEach((element) => {
          let pos_all = this.all_selected_educations
            .map(function (x) {
              return x;
            })
            .indexOf(element);
          this.all_selected_educations.splice(pos_all, 1);
        });
      this.contents.splice(index, 1);
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
    isValidFields(fields) {
      let self = this;
      let form = self.form;
      self.resetMessages();
      if (fields != "ALL") fields--;

      if (fields == "ALL" || fields == 0) {
        if (form.title.length === 0) {
          return self.showFieldsFailedError(
            0,
            self.$t("validation.required_field").replace(":NAME", this.$t("general.program_title"))
          );
        }
        if (form.description.length == 0) {
          return self.showFieldsFailedError(
            0,
            self.$t("validation.required_field").replace(":NAME", this.$t("messages.description"))
          );
        }
      }
      if (
        (fields == "ALL" || fields == 1) &&
        self.all_selected_educations.length == 0
      ) {
        return self.showFieldsFailedError(
          1,
          self.$t("validation.required_field").replace(":NAME", this.$t("messages.content"))
        );
      }

      return true;
    },
    showFieldsFailedError(index, message) {
      this.errorMessages.push(message);
      this.scrollToTop();
      this.switcherChange(index);
      return false;
    },
    validateAndSwitch(index) {
      if (this.isValidFields(index)) this.switcherChange(index);
    },
    switcherChange(index) {
      UIkit.switcher($("#uk-switcher-tab")[0]).show(index);
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getProgramItem();
    }
    this.all_selected_educations = [];
    this.$forceUpdate();
    setTimeout(() => {
      this.showModalLearning = true;
    }, 1000);
  },
  beforeDestroy() {
    const modal = UIkit.modal("#modal_learning");
    if (modal) modal.$destroy(true);
  },
  watch: {
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2500);
  },
};
</script>

<style scoped>
.program-au-page {
  --program-primary: var(--primaryColor, #3e416d);
  --program-text: #1e293b;
  --program-muted: #64748b;
  --program-border: #e2e8f0;
  min-width: 0;
}

/* Alert */
.program-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--program-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.program-au-page .uk-alert {
  margin: 0;
}

.program-au-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.program-au-page .uk-alert-danger p {
  color: #dc3545;
}

.program-au-page .uk-alert-success p {
  color: #28a745;
}

/* Header */
.program-au-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.program-au-title {
  margin: 0;
  color: var(--program-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

/* Card */
.program-au-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: visible;
  margin-top: 0;
  border: 1px solid var(--program-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Tabs */
.program-au-tabs {
  margin: 0;
  max-width: 100%;
  background: #fff;
}

.program-au-page ::v-deep .program-au-tabs.uk-tab > li > a {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 1.5rem;
}

.program-au-page ::v-deep .program-au-tabs.uk-tab > li > a:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.25);
  outline-offset: -3px;
}

/* Form */
.program-au-form {
  min-width: 0;
}

.program-au-form .col-form-label {
  color: var(--program-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.program-au-form .form-control {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  color: var(--program-text);
  font-size: 15px;
  line-height: 1.5;
}

.program-au-form input.form-control {
  -webkit-appearance: none;
  appearance: none;
}

.program-au-form textarea.form-control {
  height: auto;
  min-height: 96px;
  -webkit-appearance: none;
  appearance: none;
}

.program-au-form .form-control:focus {
  border-color: var(--program-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
  outline: none;
}

/* Footer */
.program-au-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  float: none;
  clear: both;
  padding: 16px 0 0;
}

/* Buttons */
.program-au-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 38px;
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-au-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.program-au-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.program-au-btn:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.28);
  outline-offset: 2px;
}

.program-au-chapter-btn {
  min-height: 40px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-au-chapter-btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Section items */
.program-section-list {
  cursor: move;
}

.program-section-header {
  overflow: hidden;
  min-width: 0;
}

.program-section-row {
  width: 100%;
  min-width: 0;
}

.program-section-icon {
  font-size: 150%;
  text-align: center;
  margin-top: 8px;
}

.program-section-name {
  padding: 0;
  min-width: 0;
  overflow: hidden;
}

.program-section-name input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  color: var(--program-text);
  font-size: 15px;
  line-height: 1.5;
}

.program-section-name input:focus {
  border-color: var(--program-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.16);
  outline: none;
}

.program-section-actions {
  text-align: end;
}

.program-section-action-btn {
  color: #fff;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-section-action-btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Curriculum accordion */
.program-au-page ::v-deep .c-curriculum {
  margin: 0;
  padding: 0;
  list-style: none;
}

.program-au-page ::v-deep .c-curriculum > li {
  background: #f6f7fb;
  border: 1px solid var(--program-border);
  border-radius: 10px;
  padding: 12px;
}

.program-au-page ::v-deep .c-curriculum .uk-accordion-title {
  padding: 4px 6px;
  font-size: 15px;
  font-weight: 500;
  color: var(--program-text);
}

/* Sec list items (education rows inside accordion) */
.program-au-page ::v-deep .sec-list-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  overflow: visible;
  background: #fff;
  border: 1px solid var(--program-border);
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
}

.program-au-page ::v-deep .sec-list-item > div {
  display: flex;
  align-items: center;
}

.program-au-page ::v-deep .sec-list-item > div > p {
  margin: 0;
}

.program-au-page ::v-deep .sec-list-item .btn-act a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  border-radius: 8px;
  color: #dc3545;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Education badge */
.program-edu-badge {
  position: absolute;
  top: -9px;
  left: 10px;
  z-index: 1;
  font-size: 0.7rem;
  background: #3e416d;
  padding: 1px 7px;
  border-radius: 3px;
  color: white;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
}

/* Overview */
.program-au-overview {
  max-width: 720px;
  margin: 24px auto 0;
  padding: 0 8px;
}

.program-au-overview__status {
  text-align: center;
  margin-bottom: 16px;
}

.program-au-overview__icon {
  margin-bottom: 8px;
  font-size: 32px;
  line-height: 1;
}

.program-au-overview__title {
  color: var(--program-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.4;
}

.program-au-overview__table-wrap {
  width: 100%;
  max-width: 100%;
}

.program-result-table {
  width: 100%;
  min-width: 0;
}

.program-result-label {
  width: 40%;
  color: var(--program-muted);
  font-size: 14px;
  font-weight: 500;
  text-align: right;
}

.program-result-value {
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* Modal */
.program-modal-header {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 24px !important;
  text-align: left;
  border-top: none !important;
}

/* Kill UIkit clearfix pseudo-elements that break flexbox */
.program-modal-header::before,
.program-modal-header::after {
  display: none !important;
  content: none !important;
}

.program-modal-title {
  margin: 0;
  float: none !important;
  color: var(--program-text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}

.program-modal-close {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 4px;
  float: none !important;
  min-height: 38px;
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-modal-close:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.program-modal-close:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.28);
  outline-offset: 2px;
}

.program-modal-body {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  height: calc(100vh - 56px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Misc */
input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.program-au-page ::v-deep .uk-alert-close {
  min-width: 44px;
  min-height: 44px;
}

/* ==================== Mobile (959px) ==================== */
@media (max-width: 959px) {
  .program-au-page {
    box-sizing: border-box;
    max-width: 100%;
    margin: 0;
    padding: 0 0 24px;
  }

  .program-au-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
  }

  .program-au-card {
    overflow: visible;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .program-au-page .uk-alert p {
    font-size: 16px;
    line-height: 1.5;
  }

  /* Tabs mobile */
  .program-au-page ::v-deep .program-au-tabs.uk-tab {
    display: flex;
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding: 8px 12px 0;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .program-au-page ::v-deep .program-au-tabs.uk-tab::-webkit-scrollbar {
    display: none;
  }

  .program-au-page ::v-deep .program-au-tabs.uk-tab > li {
    flex: 1 1 0;
    float: none;
    width: auto;
    min-width: 0;
  }

  .program-au-page ::v-deep .program-au-tabs.uk-tab > li > a {
    min-height: 48px;
    padding: 8px 12px;
    font-size: 16px;
    font-weight: 500;
  }

  .program-au-page ::v-deep .program-au-tabs.uk-tab > li > a i {
    font-size: 20px;
    margin-right: 0;
  }

  .program-au-page ::v-deep .program-au-tabs.uk-tab > li > a b {
    font-weight: 600;
    white-space: nowrap;
  }

  /* Form mobile */
  .program-au-form.card-body {
    padding: 16px;
  }

  .program-au-form .row {
    margin-right: 0;
    margin-left: 0;
  }

  .program-au-form .row > [class*="col-"] {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 0;
    padding-left: 0;
  }

  .program-au-form .col-form-label {
    display: block;
    margin-bottom: 8px;
    padding: 0;
    font-size: 14px;
    font-weight: 500;
  }

  .program-au-form .form-control {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .program-au-form textarea.form-control {
    height: auto;
    min-height: 120px;
    font-size: 16px;
    line-height: 1.4;
  }

  /* Section items mobile */
  .program-section-header {
    margin-right: 0;
    overflow: visible;
  }

  .program-section-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: 0;
    margin-right: 0;
    min-width: 0;
    width: 100%;
  }

  .program-section-icon {
    display: none;
  }

  .program-section-name {
    flex: 0 0 100%;
    max-width: 100%;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;
    box-sizing: border-box;
  }

  .program-section-name input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .program-section-actions {
    flex: 0 0 100%;
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    text-align: left;
    padding-left: 0;
    padding-right: 0;
  }

  .program-section-action-btn {
    min-height: 44px;
    padding: 10px 16px;
    font-size: 16px;
    border-radius: 10px;
  }

  .program-section-action-btn .ml-1 {
    margin-left: 0 !important;
  }

  .program-au-chapter-btn {
    min-height: 48px;
    font-size: 16px;
    font-weight: 600;
  }

  /* Curriculum accordion mobile */
  .program-au-page ::v-deep .c-curriculum > li {
    padding: 12px 8px;
  }

  .program-au-page ::v-deep .c-curriculum .uk-accordion-title {
    font-size: 16px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  /* Sec list items mobile */
  .program-au-page ::v-deep .sec-list-item {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px;
    font-size: 16px;
  }

  .program-au-page ::v-deep .sec-list-item > div:first-child {
    flex: 1 1 0;
    min-width: 0;
    flex-wrap: wrap;
    gap: 4px;
  }

  .program-au-page ::v-deep .sec-list-item > div:first-child > p {
    word-break: break-word;
  }

  .program-au-page ::v-deep .sec-list-item .btn-act a {
    min-width: 44px;
    min-height: 44px;
  }

  /* Education badge mobile */
  .program-edu-badge {
    top: -9px;
    left: 8px;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 3px;
  }

  /* Footer mobile */
  .program-au-footer {
    flex-direction: column;
    align-items: stretch;
    padding: 16px 0 0;
  }

  .program-au-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  /* Overview mobile */
  .program-au-overview {
    max-width: 100%;
    margin: 8px 0 0;
    padding: 0;
  }

  .program-au-overview__title {
    font-size: 20px;
    font-weight: 600;
  }

  .program-result-label {
    width: 38%;
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    vertical-align: top;
  }

  .program-result-value {
    font-size: 16px;
    line-height: 1.4;
  }

  .program-result-table td {
    padding: 12px 8px;
  }

  /* Modal mobile */
  .program-modal-header {
    padding: 10px 16px !important;
    gap: 8px;
  }

  .program-modal-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .program-modal-close {
    min-width: 44px;
    min-height: 44px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
  }

  .program-modal-body {
    height: calc(100vh - 56px);
    padding: 0;
  }

  .program-au-page ::v-deep .program-modal .uk-modal-dialog {
    margin: 0;
    padding: 0;
  }
}

/* ==================== Hover ==================== */
@media (hover: hover) and (pointer: fine) {
  .program-au-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }

  .program-au-chapter-btn:hover {
    opacity: 0.92;
    cursor: pointer;
  }

  .program-section-action-btn:hover {
    opacity: 0.92;
    cursor: pointer;
  }

  .program-modal-close:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }
}

@media (hover: none) {
  .program-au-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

/* ==================== Reduced Motion ==================== */
@media (prefers-reduced-motion: reduce) {
  .program-au-btn:active:not(:disabled),
  .program-au-chapter-btn:active,
  .program-section-action-btn:active,
  .program-modal-close:active:not(:disabled) {
    transform: none;
  }
}
</style>
